import { useEffect, useState } from 'react';

/**
 * Categorical series colors.
 * Neighbors differ in hue and lightness (Paul Tol / Okabe–Ito practice),
 * with the brand teal first, so multi-series charts stay distinct without
 * a high-chroma rainbow.
 */
export const CHART_COLORS = [
  '#0F766E',
  '#3B6FD8',
  '#E07A3D',
  '#6D5BD0',
  '#C23B5A',
  '#2F9E78',
  '#C4A035',
  '#7A8494',
];

export const LIGHT_CHART_THEME = {
  grid: '#E7EDF3',
  tick: '#64748B',
  axis: '#475569',
  cursor: '#F1F5F9',
  cursorLine: '#CBD5E1',
  dotFill: '#FFFFFF',
  surface: '#FFFFFF',
  tooltipBg: '#FFFFFF',
  tooltipBorder: '#E2E8F0',
  tooltipText: '#0F172A',
  tooltipMuted: '#64748B',
  tooltipShadow: '0 10px 24px rgb(15 23 42 / 0.08)',
  sankeyLabel: '#334155',
};

export const DARK_CHART_THEME = {
  grid: '#1E293B',
  tick: '#94A3B8',
  axis: '#CBD5E1',
  cursor: '#1E293B',
  cursorLine: '#475569',
  dotFill: '#030712',
  surface: '#030712',
  tooltipBg: '#0F172A',
  tooltipBorder: '#334155',
  tooltipText: '#F8FAFC',
  tooltipMuted: '#94A3B8',
  tooltipShadow: '0 10px 24px rgb(0 0 0 / 0.45)',
  sankeyLabel: '#E2E8F0',
};

export function useChartTheme() {
  const [isDark, setIsDark] = useState(
    () => typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
  );

  useEffect(() => {
    const root = document.documentElement;
    const sync = () => setIsDark(root.classList.contains('dark'));
    const observer = new MutationObserver(sync);
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  return isDark ? DARK_CHART_THEME : LIGHT_CHART_THEME;
}

export function seriesColor(index) {
  return CHART_COLORS[index % CHART_COLORS.length];
}

export function formatChartValue(value) {
  if (value == null || value === '') return '—';
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return '—';
    const abs = Math.abs(value);
    if (abs !== 0 && abs < 0.01) {
      return new Intl.NumberFormat(undefined, { maximumSignificantDigits: 2 }).format(value);
    }
    if (abs >= 10000) {
      return new Intl.NumberFormat(undefined, {
        notation: 'compact',
        maximumFractionDigits: 1,
      }).format(value);
    }
    return new Intl.NumberFormat(undefined, {
      maximumFractionDigits: abs >= 100 ? 1 : 2,
      useGrouping: false,
    }).format(value);
  }
  if (Array.isArray(value)) return value.map(formatChartValue).join(', ');
  return String(value);
}

export function formatAxisTick(value) {
  if (typeof value === 'number' && Number.isFinite(value)) return formatChartValue(value);
  const text = value == null ? '' : String(value);
  if (text.length <= 16) return text;
  return `${text.slice(0, 15)}…`;
}

/** Tooltip heading: the full category or number. Axis ticks stay shortened. */
export function formatTooltipLabel(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return '—';
    const abs = Math.abs(value);
    if (abs !== 0 && abs < 0.01) {
      return new Intl.NumberFormat(undefined, { maximumSignificantDigits: 2 }).format(value);
    }
    return new Intl.NumberFormat(undefined, {
      maximumFractionDigits: abs >= 100 ? 1 : 2,
      useGrouping: abs >= 10000,
    }).format(value);
  }
  return String(value);
}

/**
 * Sources sit on the left, sinks on the right.
 * Nodes that both receive and send flow are labeled above the node so the name stays out of the link corridor.
 */
export function sankeyLabelPlacement(payload) {
  const hasIncoming = (payload?.sourceNodes?.length ?? 0) > 0;
  const hasOutgoing = (payload?.targetNodes?.length ?? 0) > 0;
  if (hasIncoming && hasOutgoing) return 'above';
  if (hasIncoming) return 'right';
  return 'left';
}

export function seriesLabel(name) {
  if (name == null || name === '') return '';
  const text = String(name);
  if (text === 'count') return 'Count';
  return text;
}

/** Line and scatter: pad the data range so the shape stays readable, and keep zero when it is nearby. */
export function paddedDomain([min, max]) {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return [0, 1];
  if (min === max) {
    const pad = Math.abs(min) * 0.08 || 1;
    return [min - pad, max + pad];
  }
  const span = max - min;
  const pad = span * 0.08;
  const lower = min >= 0 && min <= span * 0.25 ? 0 : min - pad;
  const upper = max <= 0 && Math.abs(max) <= span * 0.25 ? 0 : max + pad;
  return [lower, upper];
}

/** Bars and histograms include zero so length stays comparable. */
export function zeroBasedDomain([min, max]) {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return [0, 1];
  const lower = Math.min(0, min);
  const upper = Math.max(0, max);
  const span = upper - lower || 1;
  const headroom = span * 0.08;
  return [
    lower === 0 ? 0 : lower - headroom,
    upper === 0 ? 0 : upper + headroom,
  ];
}
